import type { BillingBannerState, SubscriptionCardState } from '@parsleyai/ui'
import { useContext, useEffect, useRef, useState } from 'react'
import { toCardState } from '@/pages/settings/subscriptionCardState'
import { BillingContext } from '@/store/userData/billing/BillingContext'
import type { BillingSubscriptionDoc } from '@/store/userData/billing/types'
import { bannerHoldsSlot, toBannerState } from './billingBannerState'

/** How long the recovery confirmation stays up. Matches the ~2.5s the DS stories specify. */
export const RECOVERED_MS = 2500

export interface BillingBannerView {
  /** What the banner should render. `none` means nothing. */
  state: BillingBannerState
  /** The settings page's vocabulary, kept so callers can route the action the same way it does. */
  cardState: SubscriptionCardState
  sub: BillingSubscriptionDoc | null
  nowMs: number
}

/**
 * Resolve the banner's state from the billing slice. Shared by the banner and `AppConnectionBanner`
 * (which asks whether billing claimed the slot), because the two mount in different places.
 */
export function useBillingBanner(): BillingBannerView {
  // `useContext` rather than `useGetBilling`, which throws outside the provider. Both callers are
  // chrome, and absent context reads as "nothing to say" — the safe answer for a banner.
  const billing = useContext(BillingContext)
  const state = billing?.state
  const nowMs = Date.now()
  const cardState = state && billing ? toCardState(state, billing.entitlement, nowMs) : 'loading'
  const resolved = toBannerState(cardState, state?.subscription ?? null, nowMs)
  const recovered = useRecovery(cardState, resolved)

  return { state: recovered ? 'recovered' : resolved, cardState, sub: state?.subscription ?? null, nowMs }
}

/**
 * Show the recovery confirmation when a payment problem clears in-app — a Smart Retry succeeding hours
 * later would otherwise just make the amber bar vanish. `loading` clears it, since that's sign-out.
 */
function useRecovery(cardState: SubscriptionCardState, resolved: BillingBannerState): boolean {
  const sawJeopardy = useRef(false)
  const [showing, setShowing] = useState(false)

  useEffect(() => {
    if (cardState === 'loading') {
      sawJeopardy.current = false
      return
    }
    if (bannerHoldsSlot(resolved)) {
      sawJeopardy.current = true
      return
    }
    // Only a genuinely healthy subscription counts as recovery. `paused`, `canceled` and the
    // terminal states also leave jeopardy, but nothing good happened.
    const healthy = cardState === 'active' || cardState === 'cancel_pending' || cardState === 'trialing'
    if (!healthy || !sawJeopardy.current) return

    sawJeopardy.current = false
    setShowing(true)
    const timer = setTimeout(() => setShowing(false), RECOVERED_MS)
    return () => clearTimeout(timer)
  }, [cardState, resolved])

  return showing
}
